import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { useFormik } from 'formik'
import { FaCar } from 'react-icons/fa'
import {
    BsBriefcase, BsCardHeading, BsClockHistory, BsDiagram3, BsFileEarmarkMedicalFill, BsGearFill, BsJournalText, BsPeopleFill, BsPlusCircle, BsSave, BsSendFill,
    BsShieldCheck
} from 'react-icons/bs'
import Mensajes from '../../../mensajes'
import {
    ACCION_BORRADOR, ACCION_ENVIAR, ESTADO_BORRADOR, ESTADO_ENVIADO, ESTADO_REVISADO, ETIQUETA_ACTUALIZAR_INFORME,
    MODO_SOLO_LECTURA, MSG_INFORME_PENDIENTE_REVISION, MSG_SECCION7_PENDIENTE, OBLIGATORIOS_ENVIAR, OBLIGATORIOS_ENVIAR_CON_FISCALIA,
    TITULO_ACTUALIZAR_INFORME, TITULO_INFORME_INICIAL, esquemaInforme
} from './constantes'

// Segundos que se muestra el aviso de éxito dentro del formulario antes de que este se cierre solo.
const TIEMPO_TOAST_MS = 3000
// Los avisos de error duran más: hay que leerlos y corregir.
const TIEMPO_ERROR_MS = 8000

const aTexto = (valor) => (valor === null || valor === undefined ? '' : String(valor))

// Valores iniciales: el último informe del caso precargado (los ids numéricos se pasan a texto para los combos) o en blanco.
const valoresIniciales = (informe) => ({
    tipoDelito: aTexto(informe?.tipoDelito),
    intervinoTransito: aTexto(informe?.intervinoTransito),
    agravante: aTexto(informe?.agravante),
    nroLesionados: informe ? aTexto(informe.nroLesionados) : '0',
    nroMuertos: informe ? aTexto(informe.nroMuertos) : '0',
    gradoResponsabilidad: aTexto(informe?.gradoResponsabilidad),
    licenciaVigente: aTexto(informe?.licenciaVigente),
    categoriaLicencia: aTexto(informe?.categoriaLicencia),
    vigenciaLicencia: aTexto(informe?.vigenciaLicencia),
    hipotesisAsegurado: aTexto(informe?.hipotesisAsegurado),
    hipotesisTercero: aTexto(informe?.hipotesisTercero),
    hechos: aTexto(informe?.hechos),
    estrategia: aTexto(informe?.estrategia),
    instanciaJudicial: aTexto(informe?.instanciaJudicial),
    radicado: aTexto(informe?.radicado)
})

// Versión del informe que muestra el formulario: la del último informe, o la siguiente si el último ya fue revisado (se edita una nueva).
const versionDelFormulario = (estadoUltimo, informe) => {
    if (!informe) return 1
    return estadoUltimo === ESTADO_REVISADO ? informe.nroVersion + 1 : informe.nroVersion
}

// Sección 7 (Jira 58067, punto 19): oculta en el informe inicial (versión 1); desde la versión 2, solo de lectura: «Pendiente…» si el
// último está enviado, o la decisión de la última revisión.
const queMostrarSeccion7 = (estadoUltimo, seccion7, informe) => {
    if (versionDelFormulario(estadoUltimo, informe) < 2) return 'OCULTA'
    if (estadoUltimo === ESTADO_ENVIADO) return 'PENDIENTE'
    if ((estadoUltimo === ESTADO_BORRADOR || estadoUltimo === ESTADO_REVISADO) && seccion7) return 'DECISION'
    return 'OCULTA'
}

const glosaCombo = (combo, id) => {
    if (id === null || id === undefined || !Array.isArray(combo)) return ''
    const opcion = combo.find((o) => o.id === id)
    return opcion ? opcion.glosa : ''
}

// Nombre del implicado para la tabla de montos de la sección 7 (el lambda solo envía el id; el nombre sale de la sección 1).
const nombreImplicado = (implicados, id) => {
    const implicado = (implicados || []).find((i) => i.idImplicado === id)
    return implicado ? aTexto(implicado.nombre) : ''
}

const OpcionesPlanas =({ combo }) => (combo || []).map((o) => <option key={o.id} value={String(o.id)}>{o.glosa}</option>)

const OpcionesAgrupadas = ({ combo }) => (combo || []).map((grupo) => (
    <optgroup key={grupo.id} label={grupo.glosa}>
        {(grupo.items || []).map((i) => <option key={i.id} value={String(i.id)}>{i.glosa}</option>)}
    </optgroup>
))

// Aspecto del prototipo (prototipo_informe_penal.html): encabezado y secciones moradas, franja de datos del caso, «(*)» en obligatorios.
const MORADO = '#4F517A'
const MORADO_SECCION7 = '#9095b8'
// Campos que el formulario marca con «(*)»: los obligatorios al enviar más los contadores (llevan 0 por defecto).
const MARCADOS = [...OBLIGATORIOS_ENVIAR, 'nroLesionados', 'nroMuertos']

const Seccion = ({ titulo, icono, children, colorEncabezado = MORADO, sinRelleno = false }) => (
    <div className='mb-3' style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ background: colorEncabezado, color: 'white', padding: '8px 15px', fontWeight: 500, fontSize: '0.9rem' }}>
            {icono ? <span className='me-2' style={{ verticalAlign: '-2px' }}>{icono}</span> : null}{titulo}
        </div>
        <div style={{ background: 'white', padding: sinRelleno ? 0 : '15px 18px' }}>{children}</div>
    </div>
)

const DatoCaso = ({ etiqueta, valor }) => (
    <div>
        <div style={{ fontWeight: 700, color: '#333' }}>{etiqueta}</div>
        <div style={{ color: '#666' }}>{valor}</div>
    </div>
)

// Formulario «Informe Inicial - Implicado» del abogado (RG-576). `datos` es el objeto del modo `ultimo` del lambda
// defensa-fnc-obtiene-informe-penal. Los botones «Guardar Borrador» y «Enviar» solo aparecen si el padre pasa sus
// manejadores (onGuardarBorrador / onEnviar); sin ellos el formulario solo se visualiza y se puede «Volver».
const ModalInformePenal = ({ show, onHide, datos, onGuardarBorrador, onEnviar, onHistorial, onEditarImplicado, onNuevoImplicado }) => {
    // Aviso de error (validación del formulario o rechazo del servidor): se muestra como toast en la zona fija, junto a los botones.
    // Código 11 = «Atención» (naranja); cualquier otro = «Error» (rojo).
    const [errorGeneral, setErrorGeneral] = useState('')
    const [errorCodigo, setErrorCodigo] = useState(-1)
    // Petición en curso: el estado deshabilita los botones y la referencia corta de inmediato un segundo clic (el estado se actualiza después).
    const [enviando, setEnviando] = useState(false)
    const enviandoRef = useRef(false)
    // Qué acción está en curso (Guardar Borrador o Enviar): el indicador de carga va solo en el botón que se pulsó.
    const [accionEnCurso, setAccionEnCurso] = useState('')
    // Resultado satisfactorio: toast dentro del formulario abierto; al terminar el aviso el formulario se cierra solo.
    const [respuestaToast, setRespuestaToast] = useState(null)
    const ocupado = enviando || !!respuestaToast
    const soloLectura = datos?.modoFormulario === MODO_SOLO_LECTURA
    const fiscalias = datos?.fiscalias || []
    const combos = datos?.combos || {}
    // Sección 1 (prototipo): engranaje por implicado y «Ingresar nuevo Implicado». Ambos abren el modal de implicados de Caso General
    // (lo monta el padre); solo cuando el informe se puede editar.
    const puedeEditarImplicados = !soloLectura && !!onEditarImplicado && !!onNuevoImplicado

    // Con el modal abierto los valores iniciales NO se recalculan: si el informe se recarga (por ejemplo después de guardar un implicado
    // desde la sección 1) Formik no reinicia el formulario y no se borra lo que el abogado escribió. Cerrado, sí sigue al informe.
    const iniciales = useMemo(() => valoresIniciales(datos?.informe), [show ? 'abierto' : datos])

    const formik = useFormik({
        initialValues: iniciales,
        enableReinitialize: true,
        onSubmit: () => { }
    })

    // Nada de una apertura anterior debe reaparecer al volver a abrir el formulario: el componente sigue montado con el modal
    // cerrado, así que al abrir se quita el aviso de error y se reinicia el formulario a los valores guardados (sin errores,
    // sin campos «tocados» y sin texto escrito que no se guardó).
    useEffect(() => {
        if (show) {
            setErrorGeneral('')
            formik.resetForm({ values: iniciales })
        } else setRespuestaToast(null)
    }, [show])

    // El toast se oculta solo (o con su «x») y, al terminar, el formulario se cierra.
    const cierraConToast = () => {
        setRespuestaToast(null)
        onHide()
    }

    // Valida con el esquema de la acción (Borrador es más laxo que Enviar) y, si pasa, entrega los 15 atributos al padre.
    // La petición en curso se marca desde el primer instante (antes de la validación asíncrona): un segundo clic rápido no la repite.
    const intentar = async (accion, manejador) => {
        if (enviandoRef.current) return
        enviandoRef.current = true
        setEnviando(true)
        setAccionEnCurso(accion)
        setErrorGeneral('')
        try {
            try {
                await esquemaInforme(accion, fiscalias.length > 0).validate(formik.values, { abortEarly: false })
            } catch (err) {
                const errores = {}
                const detalles = err.inner || []
                detalles.forEach((e) => { if (e.path && !errores[e.path]) errores[e.path] = e.message })
                formik.setErrors(errores)
                formik.setTouched(Object.keys(formik.values).reduce((acc, k) => ({ ...acc, [k]: true }), {}), false)
                setErrorCodigo(11)
                setErrorGeneral('Revise los campos obligatorios.')
                return
            }

            const respuesta = await manejador({ accion, idInforme: datos?.informe?.estado === ESTADO_BORRADOR ? datos.informe.idInforme : null, informe: formik.values })
            if (respuesta && respuesta.message) setRespuestaToast(respuesta)
        } catch (err) {
            setErrorCodigo(err.code === 11 ? 11 : -1)
            setErrorGeneral(err.message)
        } finally {
            enviandoRef.current = false
            setEnviando(false)
            setAccionEnCurso('')
        }
    }

    const handleFiscalia = (e) => {
        const instancia = e.target.value
        const fiscalia = fiscalias.find((f) => String(f.instancia) === instancia)
        formik.setFieldValue('instanciaJudicial', instancia)
        formik.setFieldValue('radicado', fiscalia ? aTexto(fiscalia.radicado) : '')
    }

    // «(*)» en rojo si el campo es obligatorio (la fiscalía y el radicado solo cuando el caso tiene fiscalías).
    const esMarcado = (nombre) => MARCADOS.includes(nombre) || (fiscalias.length > 0 && OBLIGATORIOS_ENVIAR_CON_FISCALIA.includes(nombre))
    const etiquetaCampo = (nombre, etiqueta) => (
        <Form.Label className='fw-bold' style={{ fontSize: '0.85rem' }}>
            {etiqueta}{esMarcado(nombre) ? <span style={{ color: '#c94747', marginLeft: 3 }}>(*)</span> : null}
        </Form.Label>
    )

    const campoSelect = (nombre, etiqueta, opciones, md = 3) => (
        <Col md={md} className='mb-2'>
            {etiquetaCampo(nombre, etiqueta)}
            <Form.Select
                name={nombre}
                value={formik.values[nombre]}
                onChange={formik.handleChange}
                disabled={soloLectura}
                isInvalid={!!(formik.touched[nombre] && formik.errors[nombre])}
            >
                <option value=''>Seleccione...</option>
                {opciones}
            </Form.Select>
            <Form.Control.Feedback type='invalid'>{formik.errors[nombre]}</Form.Control.Feedback>
        </Col>
    )

    const campoTexto = (nombre, etiqueta, md = 3, props = {}) => (
        <Col md={md} className='mb-2'>
            {etiqueta ? etiquetaCampo(nombre, etiqueta) : null}
            <Form.Control
                name={nombre}
                value={formik.values[nombre]}
                onChange={formik.handleChange}
                disabled={soloLectura || props.readOnly}
                isInvalid={!!(formik.touched[nombre] && formik.errors[nombre])}
                {...props}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors[nombre]}</Form.Control.Feedback>
        </Col>
    )

    const siNo = <><option value='S'>SI</option><option value='N'>NO</option></>
    const seccion7 = queMostrarSeccion7(datos?.estadoUltimo, datos?.seccion7, datos?.informe)
    const esActualizacion = datos?.etiquetaAccion === ETIQUETA_ACTUALIZAR_INFORME
    const editable = !soloLectura && (onGuardarBorrador || onEnviar)

    return (
        <Modal show={show} onHide={onHide} size='xl' scrollable>
            <Modal.Header closeButton closeVariant='white' style={{ background: MORADO, color: 'white' }}>
                <Modal.Title style={{ fontSize: '1rem', fontWeight: 500 }}>
                    <BsFileEarmarkMedicalFill className='me-2' style={{ verticalAlign: '-2px' }} />
                    {esActualizacion ? TITULO_ACTUALIZAR_INFORME : TITULO_INFORME_INICIAL}
                </Modal.Title>
            </Modal.Header>

            {datos?.caso && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px 20px', padding: '12px 18px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', fontSize: '0.82rem' }}>
                    <DatoCaso etiqueta='Nro. Siniestro' valor={datos.caso.siniestro} />
                    <DatoCaso etiqueta='Fecha Siniestro' valor={datos.caso.fechaSiniestro} />
                    <DatoCaso etiqueta='Placa Asegurado' valor={datos.caso.placaAsegurado} />
                    <DatoCaso etiqueta='Nombre Asegurado' valor={datos.caso.nombreAsegurado} />
                    <DatoCaso etiqueta='Abogado Inicial' valor={datos.caso.abogadoInicial} />
                </div>
            )}

            <Modal.Body style={{ background: '#f4f4f8' }}>
                {soloLectura ? <Alert variant='info'>{MSG_INFORME_PENDIENTE_REVISION}</Alert> : null}

                <Seccion titulo='1. Implicados' icono={<BsPeopleFill />} sinRelleno>
                    <Table size='sm' bordered className='mb-0' style={{ fontSize: '0.85rem' }}>
                        <thead>
                            <tr>
                                <th>ID</th><th>Nombre Implicado</th><th>Calidad de la Víctima</th><th>Edad</th><th>Tipo Lesión</th><th>Fecha Actualización</th>
                                {puedeEditarImplicados ? <th style={{ width: 50 }}></th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {(datos?.implicados || []).length === 0
                                ? <tr><td colSpan={puedeEditarImplicados ? 7 : 6} className='text-center'>Sin implicados</td></tr>
                                : datos.implicados.map((i) => (
                                    <tr key={i.idImplicado}>
                                        <td>{i.idImplicado}</td><td style={{ color: '#5aacb6', fontWeight: 500 }}>{i.nombre}</td><td>{i.calidadVictima}</td>
                                        <td>{i.edad === null || i.edad === undefined ? '—' : `${i.edad} años`}</td><td>{i.tipoLesion}</td><td>{i.fechaActualizacion}</td>
                                        {puedeEditarImplicados ? (
                                            <td className='text-center'>
                                                <Button className='btn-informe-implicado' size='sm' disabled={enviando} title='Actualizar Tercero Implicado'
                                                    onClick={() => onEditarImplicado(i.idImplicado)}>
                                                    <BsGearFill />
                                                </Button>
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                    {puedeEditarImplicados ? (
                        <div className='d-flex justify-content-end p-2'>
                            <Button className='btn-informe-implicado' size='sm' disabled={enviando} onClick={onNuevoImplicado}>
                                <BsPlusCircle className='me-2' />Ingresar nuevo Implicado
                            </Button>
                        </div>
                    ) : null}
                </Seccion>

                <Seccion titulo='2. Hechos del Siniestro' icono={<FaCar />}>
                    <Row>
                        {campoSelect('tipoDelito', 'Tipo de Delito', <OpcionesPlanas combo={combos.tipoDelito} />)}
                        {campoSelect('intervinoTransito', 'Intervino Tránsito', siNo)}
                        {campoSelect('agravante', 'Agravantes', <OpcionesPlanas combo={combos.agravante} />)}
                        {campoTexto('nroLesionados', 'N° de Lesionados', 3, { type: 'number', min: 0 })}
                        {campoTexto('nroMuertos', 'N° de Muertos', 3, { type: 'number', min: 0 })}
                        {campoSelect('gradoResponsabilidad', 'Grado de Responsabilidad', <OpcionesPlanas combo={combos.gradoResponsabilidad} />)}
                    </Row>
                </Seccion>

                <Seccion titulo='3. Validación de Licencia' icono={<BsCardHeading />}>
                    <Row>
                        {campoSelect('licenciaVigente', '¿Licencia Vigente?', siNo, 4)}
                        {campoSelect('categoriaLicencia', 'Categoría Licencia', <OpcionesAgrupadas combo={combos.categoriaLicencia} />, 4)}
                        {campoTexto('vigenciaLicencia', 'Vigencia Licencia', 4, { type: 'date' })}
                    </Row>
                </Seccion>

                <Seccion titulo='4. Hipótesis de Responsabilidad' icono={<BsDiagram3 />}>
                    <Row>
                        {campoSelect('hipotesisAsegurado', 'Hipótesis Asegurado', <OpcionesAgrupadas combo={combos.hipotesisAsegurado} />, 6)}
                        {campoSelect('hipotesisTercero', 'Hipótesis Tercero', <OpcionesAgrupadas combo={combos.hipotesisTercero} />, 6)}
                    </Row>
                </Seccion>

                <Seccion titulo={<>5. Hechos <span style={{ color: '#ff9a9a', marginLeft: 3 }}>(*)</span></>} icono={<BsJournalText />}>
                    <Row>
                        {campoTexto('hechos', null, 12, {
                            as: 'textarea', rows: 8, maxLength: 5000,
                            title: 'Describa detalladamente los hechos del siniestro. Incluya resultado de la consulta RUNT si aplica: Nro. licencia, expedición, categorías, restricciones.',
                            placeholder: 'Ej: CONDUCTORA ASEGURADA SE DESPLAZA ORIENTE OCCIDENTE POR LA CALLE 70 A... [continuar con relato + resultado consulta RUNT]'
                        })}
                    </Row>
                </Seccion>

                <Seccion titulo='6. Estrategia Jurídica y Recomendaciones' icono={<BsBriefcase />}>
                    <Row>
                        {campoTexto('estrategia', 'Estrategia', 12, { as: 'textarea', rows: 4, maxLength: 2000, placeholder: 'Describa la estrategia legal integral del caso...' })}
                        <Col md={8} className='mb-2'>
                            {etiquetaCampo('instanciaJudicial', 'Fiscalía')}
                            <Form.Select
                                name='instanciaJudicial'
                                value={formik.values.instanciaJudicial}
                                onChange={handleFiscalia}
                                disabled={soloLectura}
                                isInvalid={!!(formik.touched.instanciaJudicial && formik.errors.instanciaJudicial)}
                            >
                                <option value=''>Seleccione...</option>
                                {fiscalias.map((f) => <option key={f.instancia} value={String(f.instancia)}>{f.nombre}</option>)}
                            </Form.Select>
                            <Form.Control.Feedback type='invalid'>{formik.errors.instanciaJudicial}</Form.Control.Feedback>                        </Col>
                        {campoTexto('radicado', 'No. Radicado Fiscalía', 4, { readOnly: true, placeholder: 'Ej: 11001600000202400000' })}
                    </Row>
                </Seccion>

                {seccion7 !== 'OCULTA' && (
                    <Seccion titulo='7. Decisión Compañía (la ingresa el analista)' icono={<BsShieldCheck />} colorEncabezado={MORADO_SECCION7}>
                        {seccion7 === 'PENDIENTE'
                            ? <div className='text-muted fst-italic'>{MSG_SECCION7_PENDIENTE}</div>
                            : (
                                <>
                                    <Row>
                                        <Col md={5} className='mb-2'>
                                            <Form.Label className='fw-bold' style={{ fontSize: '0.85rem' }}>Decisión de la Compañía</Form.Label>
                                            <Form.Control disabled readOnly value={glosaCombo(combos.decisionCompania, datos.seccion7.decisionCompania)} />
                                            <Form.Label className='fw-bold mt-2' style={{ fontSize: '0.85rem' }}>Fecha de Revisión</Form.Label>
                                            <Form.Control disabled readOnly value={aTexto(datos.seccion7.fechaRevision)} />
                                        </Col>
                                        <Col md={7} className='mb-2'>
                                            {(datos.seccion7.montosConciliar || []).length > 0 && (
                                                <>
                                                    <Form.Label className='fw-bold' style={{ fontSize: '0.85rem', color: MORADO }}>
                                                        Montos a Conciliar <small className='text-muted fw-normal'>(solo lectura)</small>
                                                    </Form.Label>
                                                    <Table size='sm' bordered className='mb-0'>
                                                        <thead><tr><th style={{ width: 50 }}>ID</th><th>Nombre Implicado</th><th style={{ width: 180 }}>Monto a Conciliar</th></tr></thead>
                                                        <tbody>
                                                            {datos.seccion7.montosConciliar.map((m) => (
                                                                <tr key={m.idImplicado}>
                                                                    <td>{m.idImplicado}</td>
                                                                    <td>{nombreImplicado(datos.implicados, m.idImplicado)}</td>
                                                                    <td className='text-muted'>$ {Number(m.monto).toLocaleString('es-CO')}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12} className='mb-2'>
                                            <Form.Label className='fw-bold' style={{ fontSize: '0.85rem' }}>Comentario / Instrucción al Abogado</Form.Label>
                                            <Form.Control as='textarea' rows={3} disabled readOnly value={aTexto(datos.seccion7.comentarioInstruccion)} />
                                        </Col>
                                    </Row>
                                </>
                            )}
                    </Seccion>
                )}
            </Modal.Body>

            {/* Zona fija (no se desplaza con el cuerpo): el aviso se ve estés donde estés en el formulario. */}
            {respuestaToast && (
                <div style={{ padding: '8px 16px 0' }}>
                    <Mensajes showToast handleCloseToast={cierraConToast} delay={TIEMPO_TOAST_MS} responseData={respuestaToast} />
                </div>
            )}
            {errorGeneral && !respuestaToast && (
                <div style={{ padding: '8px 16px 0' }}>
                    <Mensajes showToast handleCloseToast={() => setErrorGeneral('')} delay={TIEMPO_ERROR_MS} responseData={{ code: errorCodigo, message: errorGeneral }} />
                </div>
            )}

            <Modal.Footer style={{ background: '#f8f9fa' }}>
                {/* Colores del prototipo (amarillo para el borrador, rojo para enviar). Con !important porque el tema global de la app
                    pinta los botones sin variante de naranja y un estilo en línea no lo vence. */}
                <style type='text/css'>
                    {`
                    .btn-informe-borrador { background-color: #d4a842 !important; border-color: #d4a842 !important; color: #FFFFFF !important; }
                    .btn-informe-borrador:hover:not(:disabled) { background-color: #b89138 !important; border-color: #b89138 !important; }
                    .btn-informe-enviar { background-color: #c94747 !important; border-color: #c94747 !important; color: #FFFFFF !important; }
                    .btn-informe-enviar:hover:not(:disabled) { background-color: #a83737 !important; border-color: #a83737 !important; }
                    .btn-informe-implicado { background-color: #5aacb6 !important; border-color: #5aacb6 !important; color: #FFFFFF !important; }
                    .btn-informe-implicado:hover:not(:disabled) { background-color: #4c9aa3 !important; border-color: #4c9aa3 !important; }
                    .btn-informe-implicado:disabled { opacity: 0.65; }
                    .btn-informe-historial { background-color: #5aacb6 !important; border-color: #5aacb6 !important; color: #FFFFFF !important; }
                    .btn-informe-historial:hover:not(:disabled) { background-color: #4c9aa3 !important; border-color: #4c9aa3 !important; }
                    .btn-informe-borrador:disabled, .btn-informe-enviar:disabled, .btn-informe-historial:disabled { opacity: 0.65; }
                    `}
                </style>
                <Button variant='outline-info' disabled={enviando} onClick={onHide}>Volver</Button>
                {/* Historial de informes del caso (el abogado ve también los borradores): abre la ventana del historial encima de este modal. */}
                {onHistorial && (
                    <Button className='btn-informe-historial' disabled={enviando} onClick={onHistorial}>
                        <BsClockHistory className='me-2' />Historial Informe
                    </Button>
                )}
                {editable && onGuardarBorrador && (
                    <Button className='btn-informe-borrador' disabled={ocupado} onClick={() => intentar(ACCION_BORRADOR, onGuardarBorrador)}>
                        {enviando && accionEnCurso === ACCION_BORRADOR
                            ? <Spinner as='span' animation='border' size='sm' className='me-2' />
                            : <BsSave className='me-2' />}
                        {enviando && accionEnCurso === ACCION_BORRADOR ? 'Guardando...' : 'Guardar Borrador'}
                    </Button>
                )}
                {editable && onEnviar && (
                    <Button className='btn-informe-enviar' disabled={ocupado} onClick={() => intentar(ACCION_ENVIAR, onEnviar)}>
                        {enviando && accionEnCurso === ACCION_ENVIAR
                            ? <Spinner as='span' animation='border' size='sm' className='me-2' />
                            : <BsSendFill className='me-2' />}
                        {enviando && accionEnCurso === ACCION_ENVIAR ? 'Enviando...' : (esActualizacion ? 'Actualizar Informe' : 'Enviar')}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default ModalInformePenal
